import { WORD_TYPE } from './constants';
import { getAudioMap } from '../content/curriculum';

/**
 * Returns the onboarding collection for the given word type
 * @param {WORD_TYPE} type - Word type of the current level
 */
const getOnboardingData = (type) => {
  const { onboarding, instruction } = getAudioMap();
  const onboardingData = {
    [WORD_TYPE.WORD_VC]: {
      words: {
        target: 'mat',
        targetletters: ['mat'], // Needed by word button rendering
        distractors: ['mom'],
      },
      // Before highlighting a word
      step1: [
        onboarding.common.helpthecurioso,
        onboarding.vc.step1,
        onboarding.common.soundout,
      ],
      // Highlight target
      step2: [
        onboarding.vc.targetlong,
        onboarding.vc.target,
      ],
      // Highlight distractor
      step3: [
        onboarding.vc.distractorlong,
        onboarding.vc.distractor,
      ],
      // Click correct instruction
      final1: [
        onboarding.vc.step2,
        onboarding.common.clickhere,
      ],
      // Post land on log, give control to game
      final2: [
        onboarding.vc.target,
        onboarding.common.youregoingtohearmore,
        instruction.clicktheword,
      ],
    },
    [WORD_TYPE.WORD_COMBINATIONS]: {
      words: {
        target: 'chat',
        targetletters: ['chat'], // Needed by word button rendering
        distractors: ['than'],
      },
      // Before highlighting a word
      step1: [
        onboarding.common.helpthecurioso,
        onboarding.combinations.step1,
        onboarding.common.soundout,
      ],
      // Highlight target
      step2: [
        onboarding.combinations.targetlong,
        onboarding.combinations.target,
      ],
      // Highlight distractor
      step3: [
        onboarding.combinations.distractorlong,
        onboarding.combinations.distractor,
      ],
      // Click correct instruction
      final1: [
        onboarding.combinations.step2,
        onboarding.common.clickhere,
      ],
      // Post land on log, give control to game
      final2: [
        onboarding.combinations.target,
        onboarding.common.youregoingtohearmore,
        instruction.clicktheword,
      ],
    },
    [WORD_TYPE.AUTOMATICITY]: {
      words: {
        target: 'chop',
        targetletters: ['chop'],
        distractors: ['chap'],
      },
      // Before highlighting a word
      step1: [
        onboarding.automaticity.step1,
        onboarding.automaticity.step2,
        onboarding.automaticity.step3,
      ],
      // Highlight target
      step2: [onboarding.automaticity.step4],
      // Highlight distractor
      step3: [onboarding.automaticity.step5],
      // Click correct instruction
      final1: [onboarding.automaticity.step6],
      // Post land on log, give control to game
      final2: [
        onboarding.automaticity.step7,
        onboarding.common.youregoingtohearmore,
        instruction.clicktheword,
        onboarding.automaticity.step8,
      ],
      fail: [onboarding.automaticity.fail],
    },
    [WORD_TYPE.WORD_INFLECTED]: {
      words: {
        target: 'skies',
        targetletters: ['ski', 'es'],
        distractors: ['skys'],
      },
      step1: [
        onboarding.common.helpthecurioso,
        onboarding.inflected.step1,
      ],
      step2: [onboarding.inflected.step2],
      step3: [onboarding.inflected.step3],
      step4: [onboarding.inflected.step4],
      step5: [onboarding.inflected.step5],
      step6: [onboarding.inflected.step6],
      step7: [
        onboarding.inflected.endings_are,
        onboarding.inflected.sometimes,
        onboarding.inflected.when,
      ],
      // Click here
      final1: [onboarding.common.clickhere],
      // Post land on log, give control to game
      final2: [
        onboarding.inflected.target,
        onboarding.common.youregoingtohearmore,
        instruction.clicktheword,
      ],
      fail: [], // Only automaticity uses this
    },
    [WORD_TYPE.WORD_PREFIX]: {
      words: {
        target: 'unlike',
        targetletters: ['un', 'like'],
        distractors: ['dislike'],
      },
      step1: [
        onboarding.common.helpthecurioso,
        onboarding.prefix.step1,
      ],
      step2: [onboarding.prefix.step2],
      step3: [onboarding.prefix.step3],
      step4: [onboarding.prefix.step4],
      step5: [onboarding.prefix.step5],
      step6: [onboarding.prefix.step6],
      step7: [
        onboarding.prefix.wecan,
        onboarding.prefix.thereare,
        onboarding.prefix.sometimes,
        onboarding.prefix.when,
      ],
      // Click here
      final1: [onboarding.common.clickhere],
      // Post land on log, give control to game
      final2: [
        onboarding.prefix.target,
        onboarding.common.youregoingtohearmore,
        instruction.clicktheword,
      ],
      fail: [], // Only automaticity uses this
    },
    [WORD_TYPE.WORD_TWO_SYLLABLE]: {
      words: {
        target: 'happen',
        targetletters: ['happ', 'en'],
        distractors: ['helmet'],
        distractorletters: ['hel', 'met'],
      },
      step1: [
        onboarding.common.helpthecurioso,
        onboarding.twosyllable.step1,
      ],
      step2: [onboarding.twosyllable.step2],
      step3: [onboarding.twosyllable.step3],
      step4: [onboarding.twosyllable.step4],
      step5: [onboarding.twosyllable.step5],
      step6: [onboarding.twosyllable.step6],
      step7: [
        onboarding.twosyllable.step7,
        onboarding.twosyllable.step8,
      ],
      step8: [onboarding.twosyllable.step9],
      step9: [onboarding.twosyllable.step10],
      step10: [onboarding.twosyllable.step11],
      step11: [
        onboarding.twosyllable.step12,
        onboarding.twosyllable.step13,
        onboarding.twosyllable.step14,
      ],
      final1: [onboarding.common.clickhere],
      final2: [
        onboarding.twosyllable.target,
        onboarding.common.youregoingtohearmore,
        instruction.clicktheword,
      ],
    },
    [WORD_TYPE.WORD_MULTI_SYLLABLE]: {
      words: {
        target: 'detergent',
        targetletters: ['de', 'ter', 'gent'],
        distractors: ['deliver'],
        distractorletters: ['de', 'liv', 'er'],
      },
      step1: [
        onboarding.common.helpthecurioso,
        onboarding.multisyllable.step1,
      ],
      step2: [onboarding.multisyllable.step2],
      step3: [onboarding.multisyllable.step3],
      step4: [onboarding.multisyllable.step4],
      step5: [onboarding.multisyllable.step5],
      step6: [onboarding.multisyllable.step6],
      step7: [onboarding.multisyllable.step7],
      step8: [
        onboarding.multisyllable.step8,
        onboarding.multisyllable.step9,
      ],
      step9: [onboarding.multisyllable.step10],
      step10: [onboarding.multisyllable.step11],
      step11: [onboarding.multisyllable.step12],
      step12: [onboarding.multisyllable.step13],
      step13: [
        onboarding.multisyllable.step14,
        onboarding.multisyllable.step15,
      ],
      final1: [onboarding.common.clickhere],
      final2: [
        onboarding.multisyllable.step7, // Target
        onboarding.common.youregoingtohearmore,
        instruction.clicktheword,
      ],
    },
  };

  const result = onboardingData[type];
  if (result === null || result === undefined) {
    return onboardingData[WORD_TYPE.WORD_COMBINATIONS];
  }
  return result;
};

export default getOnboardingData;
